import { BaseClient } from '../client/BaseClient';
import { ApiResponse } from '../types';

export interface User {
  _id: string;
  name: string;
  email: string;
  role: 'super_admin' | 'owner' | 'employee';
  provider: 'credentials' | 'oauth';
  active: boolean;
  lastLogin?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export interface Token {
  _id: string;
  token: string;
  name: string;
  userId: string;
  sessionId?: string;
  permissions: string[];
  allowedPhoneNumbers: string[];
  active: boolean;
  expiresAt?: Date;
  lastUsed?: Date;
  usageCount: number;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateUserRequest {
  name: string;
  email: string;
  role: 'super_admin' | 'owner' | 'employee';
  provider: 'credentials' | 'oauth';
  password?: string; // Required for credentials provider
}

export interface UpdateUserRequest {
  name?: string;
  role?: 'super_admin' | 'owner' | 'employee';
  active?: boolean;
}

export interface CreateTokenRequest {
  name: string;
  userId: string;
  sessionId?: string;
  permissions: string[];
  allowedPhoneNumbers?: string[];
  expiresAt?: string;
}

export interface UpdateTokenRequest {
  name?: string;
  permissions?: string[];
  allowedPhoneNumbers?: string[];
  active?: boolean;
  expiresAt?: string;
}

export interface UsersResponse {
  success: boolean;
  data: {
    users: User[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface UserResponse {
  success: boolean;
  data: User;
}

export interface TokensResponse {
  success: boolean;
  data: {
    tokens: Token[];
    total: number;
    page: number;
    limit: number;
  };
}

export interface TokenResponse {
  success: boolean;
  data: Token;
}

export interface TokenCreationResponse {
  success: boolean;
  data: Token & {
    token: string; // Full token value is only shown on creation
  };
}

export class Admin {
  constructor(private client: BaseClient) {}

  // User Management
  
  /**
   * List all users
   * @param params - Query parameters for filtering and pagination
   */
  async listUsers(params?: {
    search?: string;
    role?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<UsersResponse> {
    return this.client['get']<UsersResponse>('/api/users', { params });
  }

  /**
   * Get a specific user by ID
   * @param userId - User ID
   */
  async getUser(userId: string): Promise<UserResponse> {
    return this.client['get']<UserResponse>(`/api/users/${userId}`);
  }

  /**
   * Create a new user
   * @param userData - User creation data
   */
  async createUser(userData: CreateUserRequest): Promise<UserResponse> {
    return this.client['post']<UserResponse>('/api/users', userData);
  }

  /**
   * Update an existing user
   * @param userId - User ID
   * @param updates - User update data
   */
  async updateUser(userId: string, updates: UpdateUserRequest): Promise<UserResponse> {
    return this.client['put']<UserResponse>(`/api/users/${userId}`, updates);
  }

  /**
   * Deactivate a user
   * @param userId - User ID
   */
  async deactivateUser(userId: string): Promise<{ success: boolean }> {
    return this.client['patch']<{ success: boolean }>(`/api/users/${userId}/deactivate`);
  }

  /**
   * Delete a user permanently
   * @param userId - User ID
   */
  async deleteUser(userId: string): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(`/api/users/${userId}`);
  }

  // Token Management

  /**
   * List all API tokens
   * @param params - Query parameters for filtering and pagination
   */
  async listTokens(params?: {
    search?: string;
    userId?: string;
    sessionId?: string;
    active?: boolean;
    limit?: number;
    offset?: number;
  }): Promise<TokensResponse> {
    return this.client['get']<TokensResponse>('/api/tokens', { params });
  }

  /**
   * Get a specific token by ID
   * @param tokenId - Token ID
   */
  async getToken(tokenId: string): Promise<TokenResponse> {
    return this.client['get']<TokenResponse>(`/api/tokens/${tokenId}`);
  }

  /**
   * Create a new API token
   * @param tokenData - Token creation data
   */
  async createToken(tokenData: CreateTokenRequest): Promise<TokenCreationResponse> {
    return this.client['post']<TokenCreationResponse>('/api/tokens', tokenData);
  }

  /**
   * Update an existing token
   * @param tokenId - Token ID
   * @param updates - Token update data
   */
  async updateToken(tokenId: string, updates: UpdateTokenRequest): Promise<TokenResponse> {
    return this.client['put']<TokenResponse>(`/api/tokens/${tokenId}`, updates);
  }

  /**
   * Revoke a token (set inactive)
   * @param tokenId - Token ID
   */
  async revokeToken(tokenId: string): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(`/api/tokens/${tokenId}/revoke`);
  }

  /**
   * Rotate a token (generate new token value)
   * @param tokenId - Token ID
   */
  async rotateToken(tokenId: string): Promise<TokenCreationResponse> {
    return this.client['post']<TokenCreationResponse>(`/api/tokens/${tokenId}/rotate`);
  }

  /**
   * Delete a token permanently
   * @param tokenId - Token ID
   */
  async deleteToken(tokenId: string): Promise<{ success: boolean }> {
    return this.client['delete']<{ success: boolean }>(`/api/tokens/${tokenId}`);
  }

  // Session Management

  /**
   * Assign users to a session
   * @param tel - Session phone number
   * @param userIds - Array of user IDs to assign
   */
  async assignUsersToSession(tel: string, userIds: string[]): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(`/api/sessions/${encodeURIComponent(tel)}/assign-users`, {
      userIds,
    });
  }

  /**
   * Assign tokens to a session
   * @param tel - Session phone number
   * @param tokenIds - Array of token IDs to assign
   */
  async assignTokensToSession(tel: string, tokenIds: string[]): Promise<{ success: boolean }> {
    return this.client['post']<{ success: boolean }>(`/api/sessions/${encodeURIComponent(tel)}/assign-tokens`, {
      tokenIds,
    });
  }

  // Analytics

  /**
   * Get system analytics
   * @param period - Time period ('day', 'week', 'month')
   */
  async getSystemAnalytics(period: 'day' | 'week' | 'month' = 'week'): Promise<{
    success: boolean;
    data: {
      users: {
        total: number;
        active: number;
        new: number;
      };
      tokens: {
        total: number;
        active: number;
        usage: number;
      };
      sessions: {
        total: number;
        connected: number;
        active: number;
      };
      messages: {
        sent: number;
        received: number;
        total: number;
      };
    };
  }> {
    return this.client['get']<any>('/api/admin/analytics', {
      params: { period },
    });
  }

  /**
   * Get user analytics
   * @param userId - User ID
   * @param period - Time period
   */
  async getUserAnalytics(userId: string, period: 'day' | 'week' | 'month' = 'week'): Promise<{
    success: boolean;
    data: {
      tokens: number;
      sessions: number;
      messages: number;
      lastActive: string;
      usage: Array<{
        date: string;
        requests: number;
      }>;
    };
  }> {
    return this.client['get']<any>(`/api/admin/users/${userId}/analytics`, {
      params: { period },
    });
  }

  /**
   * Get token analytics
   * @param tokenId - Token ID
   * @param period - Time period
   */
  async getTokenAnalytics(tokenId: string, period: 'day' | 'week' | 'month' = 'week'): Promise<{
    success: boolean;
    data: {
      usage: number;
      endpoints: Record<string, number>;
      errors: number;
      lastUsed: string;
      usage_history: Array<{
        date: string;
        requests: number;
      }>;
    };
  }> {
    return this.client['get']<any>(`/api/admin/tokens/${tokenId}/analytics`, {
      params: { period },
    });
  }
}